import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Save,
  Search,
  Upload,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";

import type {
  EligibilityVerdict,
  ScopeActivationResponse,
  ScopeSheetLoadError,
  ScopeSheetLoadOut,
} from "@/api/scope-activation";
import {
  acceptSuggestions,
  bulkScopeDecisions,
  listAllScopeItems,
  listScopeItems,
  loadScopeSheet,
  SCOPE_ITEM_ID_MAX_LENGTH,
  SHEET_MAX_ROWS,
  sheetLoadErrors,
} from "@/api/scope-activation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  distinctLobs,
  scopeItemRowsToCsv,
  scopeItemRowsToMatrix,
} from "../scopeActivationFilter";
import {
  type IgnoredColumnEdit,
  ignoredColumnEdits,
  MAX_SHEET_BYTES,
  readSheetFile,
} from "../scopeSheetCsv";
import { diagnosticKeys } from "@/hooks/useDiagnostic";
import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";

import {
  confidenceLabel,
  eligibilityLabel,
  notePreview,
} from "./aderenciaData";
import { VERDICT_META } from "./verdictMeta";
import { CatalogDrilldown } from "./CatalogDrilldown";
import { NOTE_MAX_LENGTH, ScopeItemNoteEditor } from "./ScopeItemNoteEditor";

const ALL = "__all__";
const COLUMN_COUNT = 8;
const CONFIDENCES = ["alta", "media", "baixa"];
const VERDICT_OPTIONS: EligibilityVerdict[] = [
  "activate",
  "decision",
  "out_of_scope",
];
const LIMIT = 50;

function downloadCsv(filename: string, content: string) {
  const url = URL.createObjectURL(
    new Blob(["﻿" + content], { type: "text/csv;charset=utf-8;" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function downloadXlsx(filename: string, matrix: string[][]) {
  const XLSX = await import("xlsx");
  const ws = XLSX.utils.aoa_to_sheet(matrix);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Fit-to-Standard");
  XLSX.writeFile(wb, filename);
}

/** O `t` do `useTranslation`, para o texto dos erros sair fora do componente. */
type TranslateFn = ReturnType<typeof useTranslation>["t"];

/** Quantos erros da carga o diálogo lista; o resto vira "e mais N". */
const MAX_LISTED_ERRORS = 20;

/** Quanto a carga espera a releitura da tabela antes de liberar os editores. */
const SHEET_REFRESH_TIMEOUT_MS = 30_000;

type SheetLoadResult =
  | {
      kind: "loaded";
      result: ScopeSheetLoadOut;
      refreshFailed: boolean;
      /** Colunas que a carga não lê e em que o arquivo difere da plataforma. */
      ignoredColumns: IgnoredColumnEdit[];
    }
  | { kind: "rejected"; errors: ScopeSheetLoadError[] }
  | { kind: "failed"; detail: string | null };

/** Projeto e run de onde a carga saiu. */
interface SheetLoadScope {
  projectId: string;
  executionId: string;
}

/**
 * O resultado da carga, com o projeto e a run de onde ela saiu: a carga em voo
 * pode terminar depois da troca de projeto, e o diálogo só aparece na mesma tela.
 */
export type SheetLoadOutcome = SheetLoadResult & SheetLoadScope;

/**
 * O `detail` do servidor quando é texto e a resposta é 4xx (404, 409...); `null`
 * nos outros casos. Num 5xx o texto ("Internal Server Error") não orienta nada, e
 * a mensagem genérica diz para conferir a tabela.
 */
function serverDetail(error: unknown): string | null {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  const clientError = Math.floor((response?.status ?? 0) / 100) === 4;
  const detail = response?.data?.detail;
  return clientError && typeof detail === "string" ? detail : null;
}

/** Uma coluna ignorada pela carga: a letra, o nome quando há, e quantas linhas. */
function ignoredColumnText(t: TranslateFn, edit: IgnoredColumnEdit): string {
  const vars = { letter: edit.letter, column: edit.column, count: formatCount(edit.rows) };
  return edit.column
    ? tp(t, edit.rows, "Column {{letter}} ({{column}}): {{count}} row", "Column {{letter}} ({{column}}): {{count}} rows", vars)
    : tp(t, edit.rows, "Column {{letter}}: {{count}} row", "Column {{letter}}: {{count}} rows", vars);
}

/** Texto de um erro da carga, com a linha da planilha quando há. */
function sheetErrorText(t: TranslateFn, error: ScopeSheetLoadError): string {
  const id = error.scope_item_id ?? "";
  const message = (() => {
    switch (error.code) {
      case "too_large":
        return t("The file is larger than {{max}} MB.", { max: MAX_SHEET_BYTES / 1024 / 1024 });
      case "unclosed_quote":
        return t("A quoted cell is not closed. Close its quotes and upload again.");
      case "duplicate_header":
        return t('The column "{{value}}" appears more than once.', { value: error.value ?? "" });
      case "missing_editable_cell":
        return t("The row is missing a verdict or note cell. Use an explicit empty cell to clear a note.");
      case "too_many_rows":
        return t("The CSV has more than {{max}} rows. Split it into smaller files.", {
          max: formatCount(SHEET_MAX_ROWS),
        });
      case "id_too_long":
        return t("The scope item {{id}} exceeds {{max}} characters. Check its id.", {
          id,
          max: SCOPE_ITEM_ID_MAX_LENGTH,
        });
      case "invalid_payload":
        return t("Invalid upload data. Check the CSV fields and upload again.");
      case "invalid_encoding":
        return t("The file encoding was not recognized. Save it as CSV UTF-8 and upload again.");
      case "foreign_export":
        return t("This file was exported by another screen. Download the CSV from the Catalog and edit that one.");
      case "empty_file":
        return t("The file is empty.");
      case "missing_id_column":
        return t("The scope_item_id column is missing.");
      case "no_editable_column":
        return t("The file has neither the verdict nor the note column.");
      case "missing_id":
        return t("The scope item id is empty.");
      case "unknown_verdict":
        return t('Unknown verdict "{{value}}". Use Activate, Decision or Out of scope.', {
          value: error.value ?? "",
        });
      case "duplicate_scope_item":
        // `value` vem quando o id da linha é o de uma anterior escrito de outro
        // jeito (a planilha transformou "1E1" em "10"): o consultor só acha um "10".
        return error.value
          ? t("The scope item {{id}} is the same as {{value}}, which appears earlier in the file.", {
              id,
              value: error.value,
            })
          : t("The scope item {{id}} appears more than once.", { id });
      case "note_too_long":
        return t("The note of {{id}} is longer than {{max}} characters.", {
          id,
          max: formatCount(NOTE_MAX_LENGTH),
        });
      case "unknown_scope_item":
        return t("The scope item {{id}} is not in this run.", { id });
    }
  })();
  return error.line == null ? message : t("Line {{line}}: {{message}}", { line: error.line, message });
}

/**
 * Explorador dos scope items — paginado server-side (indexado): a tabela lê uma
 * página do banco (não o payload inteiro do run), com filtros/busca no servidor.
 * Cada linha traz o veredito EFETIVO (analista → senão sugestão), editável. O
 * "Accept all suggestions" materializa o conjunto filtrado inteiro no servidor.
 */
export function CatalogView({
  data,
  projectId,
  executionId,
  countryOnly = true,
  approved = false,
  staged,
  setStaged,
  loadOutcome,
  setLoadOutcome,
  initialVerdict,
  initialConfidence,
  initialCleanCore = false,
}: {
  data: ScopeActivationResponse;
  projectId?: string;
  executionId?: string;
  countryOnly?: boolean;
  approved?: boolean;
  /** Filtros de partida quando o catálogo é aberto por um totalizador da Visão
   *  Geral: a lista já chega com os itens que o card conta. */
  initialVerdict?: string;
  initialConfidence?: string;
  initialCleanCore?: boolean;
  // Edição pendente (staged) vive no workbench — compartilhada com a aba
  // Consolidated (que aprova e precisa saber se há não-salvos).
  staged: Record<string, EligibilityVerdict>;
  setStaged: React.Dispatch<
    React.SetStateAction<Record<string, EligibilityVerdict>>
  >;
  // Resultado da carga do CSV: vive no workbench para sobreviver à troca de aba
  // (o catálogo desmonta, mas a mutação termina e grava o resultado aqui).
  loadOutcome: SheetLoadOutcome | null;
  setLoadOutcome: (outcome: SheetLoadOutcome | null) => void;
}) {
  const { t } = useTranslation();
  const [verdict, setVerdict] = useState<string>(initialVerdict ?? ALL);
  const [lob, setLob] = useState<string>(ALL);
  const [confidence, setConfidence] = useState<string>(
    initialConfidence ?? ALL,
  );
  const [cleanCoreOnly, setCleanCoreOnly] = useState<boolean>(initialCleanCore);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  // Esconder os já-activate: foco na revisão do que ainda falta decidir.
  const [hideActivated, setHideActivated] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (scopeItemId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(scopeItemId)) {
        next.delete(scopeItemId);
      } else {
        next.add(scopeItemId);
      }
      return next;
    });
  };
  const hasUnsaved = Object.keys(staged).length > 0;
  const [busyNotes, setBusyNotes] = useState<Set<string>>(new Set());
  const onNoteBusyChange = useCallback((scopeItemId: string, busy: boolean) => {
    setBusyNotes((previous) => {
      if (previous.has(scopeItemId) === busy) return previous;
      const next = new Set(previous);
      if (busy) next.add(scopeItemId);
      else next.delete(scopeItemId);
      return next;
    });
  }, []);

  const queryClient = useQueryClient();
  const lobs = useMemo(() => distinctLobs(data.items), [data.items]);
  const activationById = useMemo(
    () => new Map(data.items.map((i) => [i.scope_item_id, i])),
    [data.items],
  );

  // Reseta a página quando um filtro muda (senão fica numa página inexistente).
  const withReset =
    (setter: (v: string) => void) =>
    (v: string) => {
      setter(v);
      setPage(1);
    };

  const filters = {
    verdict: verdict === ALL ? undefined : (verdict as EligibilityVerdict),
    lob: lob === ALL ? undefined : lob,
    confidence: confidence === ALL ? undefined : confidence,
    search: search || undefined,
    countryOnly,
    hideActivated,
    cleanCore: cleanCoreOnly || undefined,
  };

  const { data: pageData, isFetching } = useQuery({
    queryKey: ["scope-items", projectId, executionId, filters, page],
    queryFn: () =>
      listScopeItems(projectId!, {
        executionId: executionId!,
        page,
        limit: LIMIT,
        ...filters,
      }),
    enabled: !!projectId && !!executionId,
    placeholderData: (prev) => prev,
  });

  const rows = pageData?.items ?? [];
  const total = pageData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const [confirmAcceptAll, setConfirmAcceptAll] = useState(false);

  // Após gravar no banco, atualiza a tela sem refresh: a tabela/Consolidated
  // (scope-items), o Overview + contadores das abas (scope-summary) e o
  // Diagnóstico, cujo tile conta os ativos da curadoria salva (#1344).
  const invalidate = () => Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["scope-items", projectId, executionId],
    }),
    queryClient.invalidateQueries({
      queryKey: ["scope-summary", projectId],
    }),
    ...(projectId ? [queryClient.invalidateQueries({
      queryKey: diagnosticKeys.snapshot(projectId),
    })] : []),
  ]);

  const saveDraft = useMutation({
    mutationKey: ["scope-save-draft", projectId],
    mutationFn: () =>
      bulkScopeDecisions(
        projectId!,
        Object.entries(staged).map(([scope_item_id, v]) => ({
          scope_item_id,
          verdict: v,
        })),
      ),
    onSuccess: () => {
      setStaged({});
      invalidate();
    },
  });
  const acceptAll = useMutation({
    mutationKey: ["scope-accept-all", projectId],
    mutationFn: () =>
      acceptSuggestions(projectId!, { executionId: executionId!, ...filters }),
    onSuccess: () => {
      setConfirmAcceptAll(false);
      invalidate();
    },
  });

  // CSV e Excel: os scope items do filtro de país (ou o catálogo todo, sem ele)
  // lidos do banco, com o veredito efetivo (o salvo, senão a sugestão) e a
  // observação, nas colunas da tela; independente da página e dos filtros da tabela.
  const allRows = () =>
    listAllScopeItems(projectId!, { executionId: executionId!, countryOnly });
  const exportCsv = useMutation({
    mutationFn: async () => {
      downloadCsv("aderencia-scope-items.csv", scopeItemRowsToCsv(await allRows(), activationById));
    },
  });
  const exportXlsx = useMutation({
    mutationFn: async () => {
      await downloadXlsx(
        "aderencia-scope-items.xlsx",
        scopeItemRowsToMatrix(await allRows(), activationById),
      );
    },
  });

  // Upload do CSV: o navegador lê o arquivo e o servidor grava vereditos e notas
  // de uma vez, com origem `excel_load`. O conteúdo do arquivo não é gravado;
  // fica o registro da carga.
  const fileInput = useRef<HTMLInputElement>(null);

  // O que o download escreveria agora, para o aviso das colunas que a carga não
  // lê; `null` quando a releitura falha.
  const platformMatrix = async (project: string, run: string) => {
    try {
      const rows = await listAllScopeItems(project, { executionId: run, countryOnly });
      return scopeItemRowsToMatrix(rows, activationById);
    } catch {
      return null;
    }
  };

  // Depois da carga: contadores e Diagnóstico relidos sem esperar; a tabela é a
  // que os editores mostram, então eles esperam por ela, mas no máximo
  // SHEET_REFRESH_TIMEOUT_MS. `false` quando a releitura falhou ou estourou o tempo.
  const refreshAfterLoad = async ({ projectId: project, executionId: run }: SheetLoadScope) => {
    void queryClient.invalidateQueries({ queryKey: ["scope-summary", project] });
    void queryClient.invalidateQueries({ queryKey: diagnosticKeys.snapshot(project) });
    const tableKey = ["scope-items", project, run];
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<"timeout">((resolve) => {
      timer = setTimeout(() => resolve("timeout"), SHEET_REFRESH_TIMEOUT_MS);
    });
    try {
      const refreshed = await Promise.race([
        queryClient.invalidateQueries({ queryKey: tableKey }).then(() => "done" as const),
        timedOut,
      ]);
      if (refreshed === "timeout") return false;
    } finally {
      clearTimeout(timer);
    }
    return !queryClient
      .getQueryCache()
      .findAll({ queryKey: tableKey, type: "active" })
      .some((query) => query.state.status === "error");
  };

  const uploadCsv = useMutation({
    mutationKey: ["scope-sheet-load", projectId],
    mutationFn: async ({
      file,
      projectId: project,
      executionId: run,
    }: SheetLoadScope & { file: File }): Promise<SheetLoadResult> => {
      let parsed;
      try {
        parsed = await readSheetFile(file);
      } catch {
        // O navegador não leu o arquivo (sumiu, sem permissão): nada foi enviado.
        return { kind: "failed", detail: t("Could not read the file. Choose it again.") };
      }
      if (!parsed.ok) return { kind: "rejected", errors: parsed.errors };
      // Corre junto com a carga e nunca falha: sem ela, o diálogo só não avisa.
      const platform = platformMatrix(project, run);
      try {
        const result = await loadScopeSheet(project, {
          execution_id: run,
          file_name: file.name,
          has_verdict_column: parsed.hasVerdictColumn,
          has_note_column: parsed.hasNoteColumn,
          rows: parsed.rows,
        });
        const reference = await platform;
        return {
          kind: "loaded",
          result,
          refreshFailed: false,
          ignoredColumns: reference ? ignoredColumnEdits(parsed.matrix, reference) : [],
        };
      } catch (error) {
        const errors = sheetLoadErrors(error, parsed.rows);
        if (errors) return { kind: "rejected", errors };
        throw error;
      }
    },
    // O resultado aparece na hora; a mutação só termina, e libera os editores,
    // depois da releitura da tabela ou do teto dela.
    onSuccess: async (result, { projectId: project, executionId: run }) => {
      const outcome: SheetLoadOutcome = { ...result, projectId: project, executionId: run };
      setLoadOutcome(outcome);
      if (outcome.kind !== "loaded") return;
      if (!(await refreshAfterLoad(outcome))) setLoadOutcome({ ...outcome, refreshFailed: true });
    },
    // Falha que não é 422 pode ter acontecido depois do commit (timeout, rede):
    // relê a tabela para ela mostrar o que o banco tem.
    onError: async (error, { projectId: project, executionId: run }) => {
      setLoadOutcome({
        kind: "failed",
        detail: serverDetail(error),
        projectId: project,
        executionId: run,
      });
      await refreshAfterLoad({ projectId: project, executionId: run });
    },
  });
  // Só o resultado desta tela: uma carga de outro projeto ou run que terminou
  // depois da troca não abre o diálogo aqui.
  const visibleOutcome =
    loadOutcome && loadOutcome.projectId === projectId && loadOutcome.executionId === executionId
      ? loadOutcome
      : null;

  // A gravação continua pendente mesmo se o consultor recolher a linha.
  const pendingNotes = useIsMutating({ mutationKey: ["scope-item-note", projectId] });
  // `isPending` só vale para quem disparou a mutação: ao trocar de aba o
  // catálogo remonta e não o vê. A contagem por chave vê a gravação em curso.
  const savingDraft = useIsMutating({ mutationKey: ["scope-save-draft", projectId] }) > 0;
  const acceptingAll = useIsMutating({ mutationKey: ["scope-accept-all", projectId] }) > 0;
  const uploading = useIsMutating({ mutationKey: ["scope-sheet-load", projectId] }) > 0;
  const hasNoteDraft = busyNotes.size > 0;
  // D11: o upload grava direto, então espera qualquer gravação em andamento e
  // não convive com rascunho em memória, que o "Save draft" seguinte gravaria
  // por cima da carga.
  const hasDraft = hasUnsaved || hasNoteDraft;
  const saving = pendingNotes > 0 || savingDraft || acceptingAll || uploading;
  const uploadBlocked = hasDraft || saving;
  const uploadBlockedReason = hasDraft
    ? t("Save the draft before uploading a CSV")
    : saving
      ? t("Wait for the current save to finish")
      : undefined;

  return (
    <div className="flex flex-col gap-3">
      {/* barra de filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={verdict} onValueChange={withReset(setVerdict)}>
          <SelectTrigger className="h-8 w-40 text-xs">
            <SelectValue placeholder={t("Verdict")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL} className="text-xs">
              {t("All verdicts")}
            </SelectItem>
            {VERDICT_OPTIONS.map((v) => (
              <SelectItem key={v} value={v} className="text-xs">
                {t(eligibilityLabel(v))}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={lob} onValueChange={withReset(setLob)}>
          <SelectTrigger className="h-8 w-52 text-xs">
            <SelectValue placeholder={t("LoB")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL} className="text-xs">
              {t("All LoBs")}
            </SelectItem>
            {lobs.map((l) => (
              <SelectItem key={l} value={l} className="text-xs">
                {l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={confidence} onValueChange={withReset(setConfidence)}>
          <SelectTrigger className="h-8 w-36 text-xs">
            <SelectValue placeholder={t("Confidence")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL} className="text-xs">
              {t("All confidence")}
            </SelectItem>
            {CONFIDENCES.map((c) => (
              <SelectItem key={c} value={c} className="text-xs">
                {t(confidenceLabel(c))}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={t("Search scope item...")}
            className="h-8 w-48 pl-7 text-xs"
          />
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-emerald-600"
            checked={hideActivated}
            onChange={(e) => {
              setHideActivated(e.target.checked);
              setPage(1);
            }}
          />
          {t("Hide activated")}
        </label>
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            data-testid="catalog-clean-core-only"
            className="h-3.5 w-3.5 accent-violet-600"
            checked={cleanCoreOnly}
            onChange={(e) => {
              setCleanCoreOnly(e.target.checked);
              setPage(1);
            }}
          />
          {t("Clean Core only")}
        </label>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">
            {t("{{count}} items", { count: formatCount(total) })}
            {isFetching ? " · …" : ""}
            {hasUnsaved && (
              <span className="ml-1 text-amber-600">
                · {t("{{count}} unsaved", { count: Object.keys(staged).length })}
              </span>
            )}
          </span>
          {projectId && executionId && !approved && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5"
                disabled={acceptingAll || hasUnsaved || uploading}
                title={t("Set the verdict of every filtered item to its suggestion")}
                onClick={() => setConfirmAcceptAll(true)}
              >
                <Check className="h-3.5 w-3.5" /> {t("Accept all suggestions")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5"
                disabled={!hasUnsaved || savingDraft || uploading}
                onClick={() => saveDraft.mutate()}
              >
                <Save className="h-3.5 w-3.5" /> {t("Save draft")}
              </Button>
            </>
          )}
          {projectId && executionId && (
            <>
              <input
                ref={fileInput}
                data-testid="catalog-upload-csv-file"
                type="file"
                disabled={uploadBlocked}
                accept=".csv,text/csv"
                className="sr-only"
                tabIndex={-1}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    if (uploadBlocked) {
                      setLoadOutcome({
                        kind: "failed",
                        detail: uploadBlockedReason ?? null,
                        projectId,
                        executionId,
                      });
                    } else {
                      uploadCsv.mutate({ file, projectId, executionId });
                    }
                  }
                  event.target.value = "";
                }}
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5"
                disabled={uploadBlocked}
                title={uploadBlockedReason}
                onClick={() => fileInput.current?.click()}
              >
                <Upload className="h-3.5 w-3.5" /> {t("Upload CSV")}
              </Button>
            </>
          )}

          {exportCsv.isError && (
            <span className="text-[11px] text-destructive">
              {t("Could not export the CSV.")}
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            disabled={!projectId || !executionId || exportCsv.isPending}
            onClick={() => exportCsv.mutate()}
          >
            <Download className="h-3.5 w-3.5" /> {t("CSV")}
          </Button>
          {exportXlsx.isError && (
            <span className="text-[11px] text-destructive">
              {t("Could not export the Excel file.")}
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            disabled={!projectId || !executionId || exportXlsx.isPending}
            onClick={() => exportXlsx.mutate()}
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> {t("Excel")}
          </Button>
        </div>
      </div>

      {/* tabela paginada (server-side) */}
      <div className="rounded-md border">
        <Table>
          <TableHeader className="bg-white">
            <TableRow>
              <TableHead className="w-8 text-xs" />
              <TableHead className="text-xs">{t("Scope Item")}</TableHead>
              <TableHead className="text-xs">{t("LoB")}</TableHead>
              <TableHead className="text-xs">{t("Verdict")}</TableHead>
              <TableHead className="text-xs">{t("Fit (ERP)")}</TableHead>
              <TableHead className="text-xs">{t("Confidence")}</TableHead>
              <TableHead className="text-xs">Clean Core</TableHead>
              <TableHead className="text-xs">{t("Note")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((i) => {
              const m = VERDICT_META[
                i.verdict as keyof typeof VERDICT_META
              ] ?? { label: i.verdict, badge: "" };
              const isExpanded = expanded.has(i.scope_item_id);
              return (
                <Fragment key={i.scope_item_id}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => toggleExpanded(i.scope_item_id)}
                  >
                    <TableCell className="text-xs">
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      <span className="font-mono font-semibold">
                        {i.scope_item_id}
                      </span>
                      <span className="text-muted-foreground">
                        {" "}
                        — {i.scope_item_name || "-"}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{i.lob || "-"}</TableCell>
                    <TableCell
                      className="text-xs"
                      data-testid={`verdict-cell-${i.scope_item_id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-1.5">
                        {!projectId || approved ? (
                          // aprovado = read-only (veredito congelado)
                          <span className={staged[i.scope_item_id] ? "" : "font-medium"}>
                            {t(
                              eligibilityLabel(
                                staged[i.scope_item_id] ?? i.effective_verdict,
                              ),
                            )}
                          </span>
                        ) : (
                          <Select
                            disabled={uploading}
                            value={
                              staged[i.scope_item_id] ??
                              i.effective_verdict ??
                              undefined
                            }
                            onValueChange={(v) =>
                              setStaged((prev) => ({
                                ...prev,
                                [i.scope_item_id]: v as EligibilityVerdict,
                              }))
                            }
                          >
                            <SelectTrigger
                              className={`h-7 w-32 text-xs ${staged[i.scope_item_id] ? "border-amber-400" : ""}`}
                            >
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              {VERDICT_OPTIONS.map((v) => (
                                <SelectItem key={v} value={v} className="text-xs">
                                  {t(eligibilityLabel(v))}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {/* RF-09: o veredito salvo veio da planilha. Some quando
                            o consultor salva outro na tela (source = analyst). */}
                        {i.decision_source === "excel_load" && (
                          <Badge
                            variant="outline"
                            className="whitespace-nowrap text-[10px] font-normal"
                            title={t("Saved by the spreadsheet load")}
                          >
                            {t("Excel load")}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={`text-[10px] ${m.badge}`}>
                        {t(m.label)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {t(confidenceLabel(i.confidence))}
                    </TableCell>
                    <TableCell className="text-xs">
                      {i.clean_core ? `⚠ ${t("yes")}` : ""}
                    </TableCell>
                    <TableCell
                      className="max-w-[16rem] text-xs"
                      data-testid={`note-cell-${i.scope_item_id}`}
                    >
                      {i.curation_note ? (
                        <div className="flex flex-col gap-0.5">
                          <span title={i.curation_note}>{notePreview(i.curation_note)}</span>
                          {i.show_in_report === false && (
                            <span className="text-[10px] text-amber-700">
                              {t("Internal note (not in report)")}
                            </span>
                          )}
                          {i.note_source === "excel_load" && (
                            <span
                              className="text-[10px] text-muted-foreground"
                              title={t("Saved by the spreadsheet load")}
                            >
                              {t("Excel load")}
                            </span>
                          )}
                        </div>
                      ) : projectId ? (
                        <span className="text-muted-foreground">{t("Add note")}</span>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={COLUMN_COUNT} className="bg-muted/20 p-0">
                        <div className="flex flex-col gap-2">
                          {projectId && (
                            <ScopeItemNoteEditor
                              key={`${i.note_updated_at ?? ""}|${i.curation_note ?? ""}|${i.show_in_report ?? ""}`}
                              disabled={uploading}
                              onBusyChange={onNoteBusyChange}
                              row={i}
                              projectId={projectId}
                              executionId={executionId}
                            />
                          )}
                          <CatalogDrilldown
                            row={i}
                            activationItem={activationById.get(i.scope_item_id)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  className="py-6 text-center text-xs text-muted-foreground"
                >
                  {isFetching ? t("Loading…") : t("No items for the selected filters.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* paginação */}
      <div className="flex items-center justify-end gap-3 text-xs text-muted-foreground">
        <span>{t("Page {{page}} of {{totalPages}}", { page, totalPages })}</span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          <ChevronLeft className="h-3.5 w-3.5" /> {t("Prev")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          {t("Next")} <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Confirmação: Accept all sobrescreve vereditos já confirmados (evita
          misclick que reseta o trabalho do analista). */}
      <AlertDialog open={confirmAcceptAll} onOpenChange={setConfirmAcceptAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Accept all suggestions?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {tp(
                t,
                total,
                "This sets the verdict of the {{count}} filtered item to the tree's suggestion, overwriting any verdict you've already confirmed for it. This can't be undone.",
                "This sets the verdict of the {{count}} filtered items to the tree's suggestion, overwriting any verdict you've already confirmed for them. This can't be undone.",
                { count: formatCount(total) },
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={acceptingAll || uploading}
              onClick={(e) => {
                e.preventDefault();
                acceptAll.mutate();
              }}
            >
              {t("Accept all")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Resultado do upload do CSV: o que foi gravado, ou por que nada foi. */}
      <AlertDialog
        open={visibleOutcome !== null}
        onOpenChange={(open) => {
          if (!open) setLoadOutcome(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {visibleOutcome?.kind === "loaded" ? t("CSV loaded") : t("The CSV was not loaded")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div data-testid="sheet-load-outcome" className="flex flex-col gap-1 text-xs">
                {visibleOutcome?.kind === "loaded" && (
                  <>
                    <span>
                      {t(
                        "Updated: {{verdicts}} verdicts and {{notes}} notes. Notes removed: {{removed}}. Unchanged rows: {{unchanged}}.",
                        {
                          verdicts: formatCount(visibleOutcome.result.verdicts_changed),
                          notes: formatCount(visibleOutcome.result.notes_changed),
                          removed: formatCount(visibleOutcome.result.notes_removed),
                          unchanged: formatCount(visibleOutcome.result.unchanged),
                        },
                      )}
                    </span>
                    {visibleOutcome.refreshFailed && (
                      <span>
                        {t("Saved, but the table could not be refreshed. Reload the page to see the changes.")}
                      </span>
                    )}
                    {visibleOutcome.result.locked_ignored > 0 && (
                      <span>
                        {t("Verdict changes ignored because the scope is approved: {{count}}.", {
                          count: formatCount(visibleOutcome.result.locked_ignored),
                        })}
                      </span>
                    )}
                    {visibleOutcome.ignoredColumns.length > 0 && (
                      <>
                        <span>
                          {t(
                            "The upload reads only the verdict and note columns. These columns differ from the platform and were not saved:",
                          )}
                        </span>
                        <ul className="list-disc pl-4">
                          {visibleOutcome.ignoredColumns.map((edit) => (
                            <li key={edit.letter}>{ignoredColumnText(t, edit)}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                )}
                {visibleOutcome?.kind === "rejected" && (
                  <>
                    <span>{t("Nothing was saved. Fix the lines below and upload again.")}</span>
                    <ul className="list-disc pl-4">
                      {visibleOutcome.errors.slice(0, MAX_LISTED_ERRORS).map((error, index) => (
                        <li key={index}>{sheetErrorText(t, error)}</li>
                      ))}
                    </ul>
                    {visibleOutcome.errors.length > MAX_LISTED_ERRORS && (
                      <span>
                        {t("and {{count}} more", {
                          count: formatCount(visibleOutcome.errors.length - MAX_LISTED_ERRORS),
                        })}
                      </span>
                    )}
                  </>
                )}
                {visibleOutcome?.kind === "failed" && (
                  <span>
                    {visibleOutcome.detail
                      ? t("Could not upload the CSV: {{detail}}", { detail: visibleOutcome.detail })
                      : t("Could not upload the CSV. Check the table: if nothing changed, upload it again.")}
                  </span>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>{t("Close")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
